<script>
  import {
    ConfirmModal,
    UserModal,
    Layout,
    InfoBox,
    QuotaProgress,
  } from "../../components/index";
  import { ModalType } from "../../types/modal";
  import {
    faPlus,
    faTrashAlt,
    faPen,
  } from "@fortawesome/pro-regular-svg-icons";
  import {
    faSort,
    faSortDown,
    faSortUp,
  } from "@fortawesome/pro-solid-svg-icons";
  import {
    UsersRepository,
    GroupsRepository,
    RolesRepository,
    ContractsRepository,
  } from "../../repositories/index";
  import {
    Button,
    Flex,
    FlexItem,
    Icon,
    Text,
    SimpleDataTable,
    DataCell,
    DataTableExpansion,
    DataTableExpansionContent,
    Select,
    TextField,
    SelectItem,
    Badge,
    Callout,
    ProgressBar,
    Tag,
    Dropdown,
    DropdownTrigger,
    DropdownList,
    DropdownItem,
    Page,
    PageHeader,
  } from "@gl/design-system";
  import { onMount, getContext } from "svelte";
  import { format } from "date-fns";
  import {
    _,
    contract as contractStore,
    user,
    callout,
  } from "../../stores/index";
  import { Link, navigate } from "svelte-routing";
  const { isAuthorized } = getContext("isAuthorized");
  import {
    parseUrl,
    setUrl,
    setModalParams,
    clearModalParams,
  } from "../../utils/index";
  import { config } from "../../services";

  const loading = {};

  let users = [];
  let groups = [];
  let roles = [];
  let contract;

  let modal;
  let confirmModal;
  let modalType;
  let selectedUser;

  let search = {
    group: "all",
    role: "all",
    name: "",
  };
  let order = {
    asc: true,
  };
  let searched;

  async function fetchUsers() {
    try {
      loading.users = true;
      const result = await UsersRepository.getUsers();
      users = result.list;
      searched = users;
    } finally {
      loading.users = false;
    }
  }

  async function fetchGroups() {
    try {
      loading.groups = true;
      const result = await GroupsRepository.getGroups({
        missingPolicies: true,
      });
      groups = result.list;
    } finally {
      loading.groups = false;
    }
  }

  async function fetchRoles() {
    try {
      loading.roles = true;
      const result = await RolesRepository.getRoles({ missingPolicies: true });
      roles = result.list;
    } finally {
      loading.roles = false;
    }
  }

  async function fetchContract() {
    loading.contract = true;
    try {
      contract = await ContractsRepository.getContract($contractStore.id);
    } finally {
      loading.contract = false;
    }
  }

  async function onDeleteUser(user) {
    try {
      await UsersRepository.deleteUser(user.id);
      callout.set({
        msg: $_("user.delete.callout"),
        status: "success",
        timeout: true,
      });
    } finally {
      fetchUsers();
    }
  }

  function sort(field = null, toggleAsc = true) {
    if (toggleAsc) {
      order.asc = !order.asc;
    }
    order.by = field;

    const cmpA = order.asc ? -1 : 1;
    const cmpB = order.asc ? 1 : -1;
    searched = searched.sort((a, b) => {
      const A =
        typeof a[order.by] === "string"
          ? a[order.by].toUpperCase()
          : a.metadata && a.metadata[field];
      const B =
        typeof b[order.by] === "string"
          ? b[order.by].toUpperCase()
          : b.metadata && b.metadata[field];

      return A === B ? 0 : A < B ? cmpA : cmpB;
    });
    order.icon = order.asc ? "ArrowDown" : "ArrowUp";
    setUrl({
      ...search,
      orderBy: order.by,
      orderAsc: order.asc,
      userModal: modalType,
      userId: selectedUser?.id,
    });
  }

  const handleSearch = () => {
    const upperCaseSearch = search.name ? search.name.trim().toUpperCase() : "";

    searched = users
      .filter(
        (user) => user.groups?.includes(search.group) || search.group === "all",
      )
      .filter(
        (user) => user.roles?.includes(search.role) || search.role === "all",
      )
      .filter((user) => {
        if (user.email?.toUpperCase().includes(upperCaseSearch)) return true;
        const fullName = `${user.firstName?.toUpperCase()} ${user.lastName?.toUpperCase()}`;
        if (fullName.includes(upperCaseSearch)) return true;
      });
    if (order.by) {
      sort(order.by, false);
    } else {
      setUrl({
        ...search,
        orderBy: order.by,
        orderAsc: order.asc,
        userModal: modalType,
        userId: selectedUser?.id,
      });
    }
  };

  const handleReset = () => {
    search = { role: "all", group: "all", name: "" };
    order = { asc: true };
    handleSearch();
  };

  function openModal(type, user) {
    modal = true;
    modalType = type;
    selectedUser = user;
    setModalParams("userModal", "userId", { type, id: user?.id });
  }

  function closeModal() {
    modal = false;
    selectedUser = null;
    clearModalParams("userModal", "userId");
  }

  function parseUrlParams() {
    const query = parseUrl(window.location.search);
    search = {
      ...search,
      group: query.group || "all",
      role: query.role || "all",
      name: query.name || "",
    };
    if (query.orderBy) {
      order.by = query.orderBy;
      order.asc = query.orderAsc === "true";
    }

    if (query.userModal) {
      if (query.userId) selectedUser = users.find((u) => u.id === query.userId);
      openModal(query.userModal, selectedUser);
    }
  }

  async function fetchAll() {
    loading.all = true;
    try {
      await fetchUsers();

      if ($isAuthorized("group_read")) {
        await fetchGroups();
      }
      if ($isAuthorized("role_read")) {
        await fetchRoles();
      }
      await fetchContract();
    } finally {
      loading.all = false;
    }
  }

  onMount(async () => {
    await fetchAll();
    parseUrlParams();
    handleSearch();
  });
</script>

{#if modal}
  <UserModal
    bind:active={modal}
    on:close={closeModal}
    on:success={() => {
      fetchUsers();
      callout.set({
        msg: selectedUser ? $_("user.edit.callout") : $_("user.new.callout"),
        status: "success",
        timeout: true,
      });
      closeModal();
    }}
    data={selectedUser}
    {groups}
    {roles}
    {modalType}
  />
{/if}

<ConfirmModal
  bind:active={confirmModal}
  on:close={() => {
    confirmModal = false;
    selectedUser = null;
  }}
  yes={$_("buttons.delete")}
  handleYes={() => onDeleteUser(selectedUser)}
  title={$_("delete.resource", {
    values: { resourceType: $_("common.user") },
  })}
  confirmationValue={selectedUser?.id}
>
  {#if selectedUser?.id === $user?.data.id}
    <Callout status="warning">
      {$_("users.self.delete.warning")}
    </Callout>
  {/if}

  <p>
    {@html $_("delete.warning.access2", {
      values: {
        resourceName: `${selectedUser?.firstName} ${selectedUser?.lastName}`,
      },
    })}
  </p>

  <p class="mt-4 mb-2">
    {@html $_("delete.confirmation", {
      values: {
        resourceType: $_("common.user").toLowerCase(),
        resourceId: selectedUser?.id,
      },
    })}
  </p>
</ConfirmModal>

<Layout selected="users">
  <Page title={`${$_("common.users")} | ${$_("access-manager")}`}>
    <svelte:fragment slot="header">
      <PageHeader
        heading={$_("common.users")}
        subtitle={$_("users.total", {
          values: { total: users.length },
        })}
      >
        <!-- The `{#if}`s sit inside the slot rather than around it: Svelte fixes `$$slots`
             at compile time and rejects `slot="..."` inside an `{#if}`. -->
        <svelte:fragment slot="actions">
          {#if $isAuthorized("user_create")}
            {#if config.SERVICE_ACCOUNT}
              <div class="dropdown">
                <Dropdown>
                  <DropdownTrigger
                    disabled={contract?.data?.userQuota &&
                      users.length >= contract.data.userQuota}
                  >
                    <Icon icon={faPlus} />
                    <Text>{$_("users.add_user")}</Text>
                  </DropdownTrigger>
                  <DropdownList opened={false}>
                    <DropdownItem>
                      <Button
                        on:click={() => openModal(ModalType.Post)}
                        style="text-align: left;"
                      >
                        <Text>{$_("common.standard")}</Text>
                      </Button>
                    </DropdownItem>
                    <DropdownItem>
                      <Button
                        on:click={() =>
                          openModal(ModalType.ServiceAccountPost)}
                        style="text-align: left;"
                      >
                        <Text>{$_("users.service_account")}</Text>
                      </Button>
                    </DropdownItem>
                  </DropdownList>
                </Dropdown>
              </div>
            {:else}
              <Button
                on:click={() => openModal(ModalType.Post)}
                disabled={!contract ||
                  (contract?.data?.userQuota &&
                    users.length >= contract.data.userQuota)}
              >
                <Icon icon={faPlus} />
                <Text>{$_("users.add_user")}</Text>
              </Button>
            {/if}
          {/if}
        </svelte:fragment>
      </PageHeader>

      <Flex class="mb-5 align-items-center">
        {#if groups.length}
          <FlexItem class="mr-4">
            <Select bind:value={search.group} id="group">
              <SelectItem
                label={`${$_("common.groups")}: ${$_("common.all")}`}
                value="all"
              />
              {#each groups as group}
                <SelectItem
                  label={`${$_("common.group")}: ${group.name}`}
                  value={group.id}
                />
              {/each}
            </Select>
          </FlexItem>
        {/if}

        {#if roles.length}
          <FlexItem class="mr-4">
            <Select bind:value={search.role} id="group">
              <SelectItem
                label={`${$_("common.roles")}: ${$_("common.all")}`}
                value="all"
              />
              {#each roles as role}
                <SelectItem
                  label={`${$_("common.role")}: ${role.name}`}
                  value={role.id}
                />
              {/each}
            </Select>
          </FlexItem>
        {/if}

        <FlexItem class="mr-4" style="width:300px">
          <TextField
            variant="Search"
            bind:value={search.name}
            on:keydown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
        </FlexItem>

        <FlexItem class="mr-4">
          <Button on:click={handleSearch}>
            {$_("common.search")}
          </Button>
        </FlexItem>

        <FlexItem>
          <Button outlined on:click={handleReset}>
            {$_("common.reset")}
          </Button>
        </FlexItem>
      </Flex>

      <div class="results p-2 mb-4">
        <FlexItem>
          <p role="status">
            <b>{searched ? searched.length : users.length}</b>
            {$_("common.results")}
          </p>
        </FlexItem>

        {#if loading.contract === false && contract?.data?.userQuota}
          <FlexItem pushEnd>
            <Flex class="align-items-center">
              <FlexItem>
                <b class="mr-2">{$_("quota.user")}:</b>
              </FlexItem>
              <FlexItem>
                <QuotaProgress
                  amount={users.length}
                  quota={contract.data.userQuota}
                />
              </FlexItem>
              {#if users.length >= contract.data.userQuota}
                <FlexItem>
                  <InfoBox
                    title={$_("quota.user.infobox.title")}
                    tooltipLeft="null"
                    tooltipRight="20%">{$_("quota.user.infobox.text")}</InfoBox
                  >
                </FlexItem>
              {/if}
            </Flex>
          </FlexItem>
        {/if}
      </div>
    </svelte:fragment>

    <SimpleDataTable
      let:index
      let:row
      data={searched || users}
      fullWidth
      selectable={false}
      cellsCount="4"
      loading={loading.users || loading.groups || loading.all}
      key="email"
      valign="middle"
      variant="separate"
      hasExpansions={$isAuthorized("user_delete") ||
        $isAuthorized("user_write")}
      stickyHead
    >
      <slot name="empty" slot="empty">
        <div class="gl-message" style="text-align: center">
          {$_("common.no.display")}
        </div>
      </slot>
      <DataCell
        {index}
        id={`${row.id}-name`}
        header={$_("common.name")}
        headerIcon={order.by === "firstName" && order.asc
          ? faSortUp
          : order.by === "firstName" && !order.asc
            ? faSortDown
            : faSort}
        clickableHeader
        activeHeader
        on:clickHeader={() => sort("firstName")}
        maxWidth={280}
      >
        <Link
          to={`access-manager/contracts/${$contractStore.id}/users/${row.id}/data`}
        >
          <Flex class="align-items-center">
            <b class="mr-1">{row.firstName}</b>
            {#if row.isServiceAccount}
              <Tag status="default" style="font-size: 10px;">
                {$_("users.service_account")}
              </Tag>
            {:else}
              <b>{row.lastName}</b>
            {/if}
          </Flex>
        </Link>
      </DataCell>

      <DataCell
        {index}
        header={$_("users.email_address")}
        id={`${row.id}-email`}
        maxWidth={250}
      >
        {row.email ?? "-"}
      </DataCell>

      <DataCell {index} header={$_("common.groups")} id={`${row.id}-groups`}>
        {#if row.groups}
          {#each row.groups.split(",") as group, i}
            <p class={i > 0 && "mb-2"} style="">{group}</p>
          {/each}
        {/if}
      </DataCell>

      <DataCell
        {index}
        header={$_("common.roles")}
        id={`${row.id}-roles`}
        valign="center"
      >
        {#if row.roles}
          {#each row.roles.split(",") as role, i}
            <p class="mb-2" style="">{role}</p>
          {/each}
        {/if}
        {#if row.isMaster}
          <Badge status="success" label={$_("users.master")} />
        {/if}
      </DataCell>

      <DataCell
        {index}
        id={`${row.userId}-created`}
        header={$_("common.created")}
        headerIcon={order.by === "createdAt" && order.asc
          ? faSortUp
          : order.by === "createdAt" && !order.asc
            ? faSortDown
            : faSort}
        clickableHeader
        activeHeader
        on:clickHeader={() => sort("createdAt")}
      >
        {row.metadata?.createdAt
          ? format(new Date(row.metadata.createdAt), "dd.MM.yyyy")
          : "-"}
      </DataCell>

      <DataCell
        {index}
        id={`${row.id}-modified`}
        header={$_("common.last.modified")}
        headerIcon={order.by === "modifiedAt" && order.asc
          ? faSortUp
          : order.by === "modifiedAt" && !order.asc
            ? faSortDown
            : faSort}
        clickableHeader
        activeHeader
        on:clickHeader={() => sort("modifiedAt")}
        maxWidth={250}
      >
        {row.metadata?.modifiedAt
          ? format(new Date(row.metadata.modifiedAt), "dd.MM.yyyy HH:mm:ss")
          : "-"}
        {#if row.metadata?.modifiedBy}
          <p class="text-light" style="font-size:14px">
            {$_("common.by")}
            {row.metadata.modifiedBy}
          </p>
        {/if}
      </DataCell>

      <DataTableExpansion slot="expansion" let:row>
        {#if row.isExpanded}
          <DataTableExpansionContent colspan={7}>
            <Flex class="justify-content-end">
              <FlexItem>
                {#if $isAuthorized("token_read")}
                  <Button
                    outlined
                    class="mr-2"
                    size="tiny"
                    on:click={() => {
                      navigate(`users/${row.id}/api`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("users.api.keys")}</Text>
                  </Button>
                {/if}
                {#if $isAuthorized("user_read") && $isAuthorized("policy_read")}
                  <Button
                    outlined
                    class="mr-2"
                    size="tiny"
                    on:click={() => {
                      navigate(`users/${row.id}/policies`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("layout.user.policies")}</Text>
                  </Button>
                {/if}
                {#if $isAuthorized("user_write")}
                  <Button
                    outlined
                    class="mr-2"
                    size="tiny"
                    on:click={() =>
                      openModal(
                        row.isServiceAccount
                          ? ModalType.ServiceAccountPut
                          : ModalType.Put,
                        row,
                      )}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("buttons.edit")}</Text>
                  </Button>
                {/if}

                {#if $isAuthorized("user_delete")}
                  <Button
                    outlined
                    size="tiny"
                    status="error"
                    on:click={() => {
                      selectedUser = row;
                      confirmModal = true;
                    }}
                  >
                    <Icon icon={faTrashAlt} />
                    <Text>{$_("buttons.delete")}</Text>
                  </Button>
                {/if}
              </FlexItem>
            </Flex>
          </DataTableExpansionContent>
        {/if}
      </DataTableExpansion>
    </SimpleDataTable>
  </Page>
</Layout>

<style>
  .results {
    background-color: var(--gl-background);
    border-radius: 8px;
    display: flex;
  }
</style>
